import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookOpenIcon, DocumentTextIcon, ClockIcon, UsersIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';

const DistanceEducationRules: React.FC = () => {
  const courses = [
    {
      name: '가상 홈스테이 영어회화',
      content: '미국 호스트와 화상으로 영어회화 및 문화교류',
      format: 'Zoom 실시간',
      duration: '4주 (8시간)',
      capacity: '20명',
      price: '300,000원',
      color: 'blue'
    },
    {
      name: 'AI 영어 융합과정',
      content: '영어로 배우는 AI·STEAM 융합 프로젝트',
      format: 'Zoom+LMS',
      duration: '4주 (12시간)',
      capacity: '30명',
      price: '180,000원',
      color: 'purple'
    },
    {
      name: 'Reading Horizons 리딩랩',
      content: '온라인 영어 독서 및 어휘력 향상',
      format: 'LMS 비실시간',
      duration: '8주 (16시간)',
      capacity: '50명',
      price: '150,000원',
      color: 'green'
    },
    {
      name: '글로벌 리더십 세미나',
      content: '영어토론·발표 중심의 리더십 강화',
      format: 'Zoom 실시간',
      duration: '1주 (10시간)',
      capacity: '20명',
      price: '250,000원',
      color: 'orange'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 pt-20 bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-5xl font-bold mb-6">운영 규칙</h1>
            <p className="text-xl text-blue-100">동두천글로벌인재교육센터 원격평생교육원 운영규정</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-16">
          {/* 운영규칙 */}
          <div className="bg-white rounded-xl shadow-sm p-10 mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 pb-4 border-b-2 border-blue-600">운영 규칙</h2>

            {/* 제1조 - 목적 */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">제1조 【목적】</h3>
              <p className="text-gray-700 leading-relaxed pl-4">
                본 교육원은 「평생교육법」 제24조 및 동법 시행령 제27조에 따라 비대면 원격학습을 통해
                글로벌 언어·문화교육을 제공하고, 시민의 평생학습 기회를 확대함을 목적으로 한다.
              </p>
            </div>

            {/* 제2조 - 명칭 및 위치 */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">제2조 【명칭 및 위치】</h3>
              <div className="pl-4 space-y-2 text-gray-700">
                <p><strong>명칭:</strong> 동두천글로벌인재교육센터 원격평생교육원</p>
                <p><strong>소재지:</strong> 경기도 동두천시 동두천로 97, 2층 (지행동, 동두천싸이언스타워)</p>
                <p><strong>설치자:</strong> ㈜한원글로벌</p>
              </div>
            </div>

            {/* 제3조 - 교육목표 */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">제3조 【교육목표】</h3>
              <p className="text-gray-700 leading-relaxed pl-4">
                정보통신기술(ICT)을 활용한 원격교육을 통해 학습자의 영어 의사소통 능력, 글로벌 역량,
                자기주도적 학습능력을 함양한다.
              </p>
            </div>

            {/* 제4조 - 운영형태 */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">제4조 【운영형태】</h3>
              <p className="text-gray-700 leading-relaxed pl-4">
                모든 과정은 온라인 중심(Zoom, LMS, 녹화강의, 과제제출)으로 운영한다.
              </p>
            </div>

            {/* 제5조 - 교육과정 */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">제5조 【교육과정】</h3>
              <p className="text-gray-700 leading-relaxed pl-4">
                학습과정은 연간 교육계획에 따라 운영되며, [별첨1 교육과정편성표]를 따른다.
              </p>
            </div>

            {/* 제6조 - 모집 및 등록 */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">제6조 【모집 및 등록】</h3>
              <p className="text-gray-700 leading-relaxed pl-4">
                수강신청은 교육원 홈페이지를 통해 접수하며, 신청서 작성과 학습비 납부를 완료해야 등록이 확정된다.
              </p>
            </div>

            {/* 제7조 - 학습비 및 환불 */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">제7조 【학습비 및 환불】</h3>
              <p className="text-gray-700 leading-relaxed pl-4 mb-3">
                학습비는 [별첨2 학습비 내역서]를 따르며, 환불은 「평생교육법 시행령」 제23조의2에 따른다.
              </p>
              <div className="bg-blue-50 rounded-lg p-4 ml-4">
                <ul className="space-y-2 text-gray-700">
                  <li>• 개강 전: <strong className="text-blue-700">전액 환불</strong></li>
                  <li>• 총 수업시간의 1/3 전: <strong className="text-blue-700">2/3 환불</strong></li>
                  <li>• 1/2 전: <strong className="text-blue-700">1/2 환불</strong></li>
                  <li>• 1/2 이후: <strong className="text-red-600">환불 불가</strong></li>
                </ul>
              </div>
            </div>

            {/* 제8조 - 수료기준 */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">제8조 【수료기준】</h3>
              <p className="text-gray-700 leading-relaxed pl-4">
                출석률 80% 이상 및 과제제출을 완료한 자에게 수료증을 발급한다.
              </p>
            </div>

            {/* 제9조 - 강사 */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">제9조 【강사】</h3>
              <p className="text-gray-700 leading-relaxed pl-4">
                국내외 원어민 및 원격수업 전문 강사를 위촉한다.
              </p>
            </div>

            {/* 제10조 - 시설 및 설비 */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">제10조 【시설 및 설비】</h3>
              <p className="text-gray-700 leading-relaxed pl-4">
                서버, LMS, 화상회의, 전자칠판, 영상장비 등 원격수업 운영에 필요한 모든 설비를 갖춘다.
              </p>
            </div>

            {/* 제11조 - 개인정보보호 */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">제11조 【개인정보보호】</h3>
              <p className="text-gray-700 leading-relaxed pl-4">
                온라인 수업의 개인정보 유출을 방지하고 모든 학습 콘텐츠의 저작권을 보호한다.
              </p>
            </div>

            {/* 제12조 - 기타사항 */}
            <div className="mb-0">
              <h3 className="text-xl font-bold text-gray-900 mb-3">제12조 【기타사항】</h3>
              <p className="text-gray-700 leading-relaxed pl-4">
                기타 운영에 관한 사항은 관계 법령 및 센터의 내부규정에 따른다.
              </p>
            </div>
          </div>

          {/* 학습페이지 (교육과정 안내) */}
          <div className="bg-white rounded-xl shadow-sm p-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 pb-4 border-b-2 border-blue-600">
              학습페이지 (교육과정 안내)
            </h2>

            <div className="grid gap-6 mb-8">
              {courses.map((course, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                    {/* 과정명 */}
                    <div className="lg:w-1/4">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{course.name}</h3>
                    </div>

                    {/* 교육내용 */}
                    <div className="lg:w-1/3">
                      <div className="flex items-start gap-2">
                        <BookOpenIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                        <p className="text-gray-600 text-sm">{course.content}</p>
                      </div>
                    </div>

                    {/* 운영정보 */}
                    <div className="lg:w-1/3 grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">{course.format}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">{course.duration}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <UsersIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">{course.capacity}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                        <span className="font-bold text-blue-600">{course.price}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 안내 문구 */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>※ 안내:</strong> 학습비에는 교재 및 온라인 라이선스 사용료가 포함되어 있으며,
                운영 일정 및 정원은 사정에 따라 변경될 수 있습니다.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <a
              href="/application/register"
              className="inline-block bg-blue-600 text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
            >
              지금 바로 수강 신청하기
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DistanceEducationRules;
